import { ICommission } from "../../types/commission.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = `${import.meta.env.VITE_API_URL}/comissions`;

const makeRequestDebounced = createDebounce(makeRequest, 200);

interface UpdateCommissionResult extends ApiResponseType<ICommission> {}
interface ActivationApiResponse extends ApiResponseType<ICommission[]> {
  totalItems: number;
  sdrTotal?: number;
  consultantTotal?: number;
  commissionSummary?: Array<{
    type: string;
    count: number;
    totalValue: number;
  }>;
}
export async function getCommission({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = "",
  orderBy = "-updatedAt",
  technicalConsultant = "",
  fields = "technicalConsultant, sdr, activationData.plan.subscriptionInitialDate",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );
  technicalConsultant &&
    params.append("filters[technicalConsultant]", technicalConsultant);

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    ICommission[]
  > & { totalItems: number };
  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function updateCommissionConsultant(
  commissionId: string,
  newTechnicalConsultant: string
): Promise<UpdateCommissionResult> {
  const url = `${API_URL}/${commissionId}/change-consultant`; // Rota que será criada no backend

  const result = (await makeRequest(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      technicalConsultant: newTechnicalConsultant,
    }),
  })) as UpdateCommissionResult;

  return result;
}

export async function getCommisionByConsultant(technicalConsultant: string) {
  const url = `${API_URL}/consultant/${technicalConsultant}`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<ICommission[]>
  >;
  return result;
}

export async function getCommissionByWorkOrders(technicalConsultant: string) {
  const url = `${API_URL}/commissionByWorkOrders/${technicalConsultant}`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<ICommission[]>
  >;
  return result;
}

const makeDebounceActivation = createDebounce(makeRequest, 200);
export async function getAllActivations({
  page = 1,
  itemsPerPage = "",
  technicalConsultant = "",
  search = "",
  startDate = "",
  endDate = "",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    technicalConsultant: String(technicalConsultant),
  });
  search && params.append("search", search);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());

  const url = `${API_URL}/activation?${params.toString()}`;

  const result = (await makeDebounceActivation(url)) as ActivationApiResponse;

  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems ?? result.data?.length ?? 0,
    sdrTotal: result.sdrTotal ?? 0,
    consultantTotal: result.consultantTotal ?? 0,
    commissionSummary: result.commissionSummary ?? [],
  };
}
const makeDebounceActivationPlus = createDebounce(makeRequest, 200);
export async function getAllActivationPlus({
  page = 1,
  itemsPerPage = "",
  technicalConsultant = "",
  search = "",
  startDate = "",
  endDate = "",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    technicalConsultant: String(technicalConsultant),
  });
  search && params.append("search", search);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());

  const url = `${API_URL}/plus?${params.toString()}`;
  const result = (await makeDebounceActivationPlus(url)) as ApiResponseType<
    ICommission[]
  > & {
    totalItems: number;
    sdrTotal?: number;
    consultantTotal?: number;
  };

  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems ?? result.data?.length ?? 0,
    sdrTotal: result.sdrTotal ?? 0,
    consultantTotal: result.consultantTotal ?? 0,
  };
}

const makeDebounceActivationRenew = createDebounce(makeRequest, 200);
export async function getAllActivationRenew({
  page = 1,
  itemsPerPage = 10,
  technicalConsultant = "",
}: {
  page?: number;
  itemsPerPage?: number;
  technicalConsultant?: string;
}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    technicalConsultant: String(technicalConsultant),
  });

  const url = `${API_URL}/renew?${params.toString()}`;
  const result = (await makeDebounceActivationRenew(url)) as ApiResponseType<
    ICommission[]
  > & {
    totalItems: number;
    sdrTotal: number;
    consultantTotal: number;
  };

  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems ?? result.data?.length ?? 0,
    sdrTotal: result.sdrTotal ?? 0,
    consultantTotal: result.consultantTotal ?? 0,
  };
}

export async function getCommision(
  comissionType: string,
  nameConsultant: string
) {
  const url = `${API_URL}/${comissionType}/${nameConsultant}`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<ICommission[]>
  >;
  return result;
}
